import React, { PureComponent, ReactNode } from "react";
import { AppContextType, HelpPageName } from "../../AppContext";
import {
  ICON_CHORD_MODE,
  ICON_CLOSE,
  ICON_COPY_TABS,
  ICON_EXT_LINK,
  ICON_PAUSE,
  ICON_PLAY,
  ICON_RECORD,
  ICON_REFRESH,
  ICON_SCROLLING,
  ICON_SETTINGS,
  ICON_STOP,
  ICON_TRANSCRIBE,
  inlineIcon,
} from "../../constants/iconConstants";
import { C_MAJ_TRIAD } from "../../constants/noteConstants";
import { IS_WEBM_PCM_SUPPORTED } from "../../models/AudioStream.interface";
import { getVisitorId, saveVisitorId } from "../../statemachines/storage";
import InfoCard from "../layout_reuse/InfoCard";
import NoteOverrideDemo from "../menu_options/NoteOverrideDemo";
import MusicChord from "../music_visuals/history_monitor/MusicChord";
import CloseableModal from "./CloseableModal";
import "./HelpPopup.css";
import MusicNote from "../music_visuals/history_monitor/MusicNote";
import WarningText from "../theme_reuse/WarningText";

interface Props {}
interface State {}

const sparkle = "♫"; // "\u{2728}";
const point_right = "\u{1F449}";
const point_left = "\u{1F448}";
const youtube_link = "https://www.youtube.com/watch?v=yIiMTQqFN_k&list=PLLAJPcAScsyT4p5YAJdeem0sSVmdKzZNB";

class HelpPopup extends PureComponent<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  handleCloseModal = () => {
    if (null === getVisitorId()) {
      saveVisitorId(); // the first interaction on the site
    }
    this.context.dispatch({ type: "HideHelpPage" });
  };

  // handleOpenWasmExplanation = () => {
  //   this.context.dispatch({ type: "ShowHelpPage", payload: HelpPageName.WhatIsWasm });
  // };

  // handleOpenFaqAndBugs = () => {
  //   this.context.dispatch({ type: "ShowHelpPage", payload: HelpPageName.FaqAndBugs });
  // };

  // handleOpenChordControls = () => {
  //   this.context.dispatch({ type: "ShowHelpPage", payload: HelpPageName.ChordControls });
  // };

  renderHelpContent() {
    switch (this.context.helpPageName) {
      case HelpPageName.Welcome:
        return (
          <>
            <InfoCard>
              <h3>
                <span className="hide-on-small">{sparkle}</span> Chord Noodler! {sparkle}
              </h3>
              <h4>Record or load audio files and see the chords appear in real time.</h4>
              <p>After processing, you can replay all or explore individual chords.</p>
            </InfoCard>
            <InfoCard>
              <p>
                Click any note or chord to hear it -{point_right}{" "}
                <span className="help-demo-chord">
                  <MusicChord chord={C_MAJ_TRIAD} isMostLikely />
                </span>{" "}
                {point_left}.
              </p>
              <p>
                This{" "}
                <a href={youtube_link} target="_blank" rel="noreferrer">
                  Video Tutorial {inlineIcon(ICON_EXT_LINK)}
                </a>{" "}
                explains how to get the best results -- or, read the Help page.
              </p>
              {IS_WEBM_PCM_SUPPORTED ? (
                ""
              ) : (
                <p>
                  <WarningText text="this site works best on Chrome or Edge. More info on the Help page." />
                </p>
                // <p className="warning-text-line">
                //   <span className="warning-word">Warning</span>: this site works best on Chrome or Edge. More info on the Help page.
                // </p>
              )}
            </InfoCard>
          </>
        );
      case HelpPageName.TermsOfService:
        return (
          <>
            <InfoCard>
              <h2>Terms of Service</h2>
            </InfoCard>
            <InfoCard>
              <h2>By using this website, you agree to and acknowledge the following:</h2>
              <h3>I. Usage</h3>
              <p>I.a. You agree not to use the website for anything illegal, nor to encourage anyone else to do so.</p>
              <p>
                I.b. You agree not to sue me and/or my businesses for anything related to this website, nor to engage a third party to do
                so.
              </p>
              <h3>II. Expectation of Service</h3>
              <p>
                II.a. You acknowledge that the results of this website are imperfect, and that it is only a toy and can't be held to the
                standards of a professional musician.
              </p>
              <p>II.b. You acknowledge that the website may sometimes be unavailable.</p>
              <p>
                II.c. You acknowledge that you are not entitled to features, special treatment, or to the continued operation or maintenance
                of this site, regardless of whether you have ever contributed to my tip jar.
              </p>
              <p>
                II.d. You agree that you are responsible for any damage to your computer or speaker hardware which may occur while using
                this website, for example by playing audio at too high a volume.
              </p>
              <h3>III. Ownership and Data</h3>
              <p>III.a. You agree not to copy this website's code and/or logo image(s).</p>
              <p>
                III.b. You agree that although the site does not send your audio across the network, you are ultimately responsible for the
                security of your audio assets and I am not responsible for preventing you from sharing your audio by other means. For
                example, if a browser introduces a feature by which they store all recordings you make, you agree that I am not responsible
                for preventing that.
              </p>
              <p>
                III.c. You agree that although I have made a good faith attempt to prevent any data harvesting on this site, I cannot be
                held responsible for Cloudflare automatically enabling tracking features in the future, or for preventing Cloudflare from
                keeping records of traffic visiting the site, or for preventing your operating system, ISP, government, or workplace from
                monitoring your internet traffic or storage, etc.
              </p>
            </InfoCard>
          </>
        );
      case HelpPageName.Overview:
        return (
          <>
            <InfoCard>
              <h2>Getting Started</h2>
            </InfoCard>
            <InfoCard>
              <h3>How do I use this?</h3>
              <p>
                <a href={youtube_link} target="_blank" rel="noreferrer">
                  This Video Explains {inlineIcon(ICON_EXT_LINK)}
                </a>
              </p>
              <p>Record yourself singing or playing your instrument, or choose a file to analyze and click play.</p>
              <p>
                If you're singing, you can click a note -{point_right}{" "}
                <span className="help-demo-chord">
                  <strong>
                    <MusicNote note_id="C" octave={"Three"} />
                  </strong>
                </span>{" "}
                {point_left} - beforehand to hear it and start on that pitch.
              </p>
              <p>
                Next, you can click the chords -{point_right}{" "}
                <span className="help-demo-chord">
                  <MusicChord chord={C_MAJ_TRIAD} isMostLikely />
                </span>{" "}
                {point_left} -to hear which ones sound right to you.
              </p>
            </InfoCard>
            <InfoCard>
              <h3>What's on the page?</h3>
              <p>(1) Audio Playback Controls (2) Scale Identification, (3) Chord History, and (4) Tablature Builder.</p>
            </InfoCard>
            <InfoCard>
              <h3>What do the buttons do?</h3>
              <p>Audio Playback Controls...</p>
              <ul className="help-buttons-list">
                <li>{inlineIcon(ICON_RECORD)} - start recording</li>
                <li>{inlineIcon(ICON_STOP)} - stop recording</li>
                <li>{inlineIcon(ICON_CLOSE)} - reset everything and start over</li>
                <li>(Additionally, the usual play/pause/seek controls will appear after you record or add a file.)</li>
              </ul>
              <p>Scale Identification...</p>
              <ul className="help-buttons-list">
                <li>m - mode: prefer the minor version of scales</li>
                <li>M - mode: prefer the major version of scales</li>
                <li>{inlineIcon(ICON_REFRESH)} - Reset all pinned notes</li>
                {/* <li>{inlineIcon(ICON_NOTEOVERRIDE)} - show/hide manual note override controls</li> */}
                <li>{inlineIcon(ICON_SETTINGS)} - open advanced key detection settings </li>
              </ul>
              <p>Chord History...</p>
              <ul className="help-buttons-list">
                <li>{inlineIcon(ICON_CHORD_MODE.SOUND)} - mode: clicking chords plays them</li>
                <li>{inlineIcon(ICON_CHORD_MODE.EDIT)} - mode: clicking chords opens an edit window</li>
                <li>{inlineIcon(ICON_CHORD_MODE.DELETE)} - mode: clicking chords deletes them</li>
                <li>{inlineIcon(ICON_CHORD_MODE.CHOOSE)} - mode: clicking chords sends them to the tablature builder</li>
                <li>N - show/hide the note history, to focus on the chords instead</li>
                <li>{inlineIcon(ICON_SCROLLING)} - enable or disable automatic scrolling within chord history</li>
                <li>{inlineIcon(ICON_PLAY)} - play through the chords</li>
                <li>{inlineIcon(ICON_PAUSE)} - stop playing through the chords</li>
                <li>{inlineIcon(ICON_TRANSCRIBE)} - transcribe the chords into a different key</li>
                <li>{inlineIcon(ICON_SETTINGS)} - open advanced chord history settings</li>
              </ul>
              <p>Tablature Builder...</p>
              <ul className="help-buttons-list">
                <li>{inlineIcon(ICON_COPY_TABS)} - copy tabs to the clipboard</li>
                <li>{inlineIcon(ICON_CLOSE)} - delete all the tabs and start over</li>
              </ul>
            </InfoCard>
            <InfoCard>
              <h2>Scale Identification</h2>
            </InfoCard>
            <InfoCard>
              <h3>The Twelve Notes</h3>
              <p>The loudest notes detected are shaded in darkest.</p>
              <p>Notes which aren't in the scale get crossed out, via automatic note pinning or according to your manual settings.</p>
              <p>
                Tip: Click a note -{point_right}{" "}
                <span className="help-demo-chord">
                  {/* <MusicChord chord={C_MAJ_TRIAD} /> */}
                  <MusicNote note_id="C" octave={"Three"} />
                </span>{" "}
                {point_left} - to get a starting pitch before recording a vocal melody line.
              </p>
            </InfoCard>

            <InfoCard>
              <NoteOverrideDemo />
            </InfoCard>

            <InfoCard>
              <h2>Chord & Note History</h2>
            </InfoCard>
            <InfoCard>
              <h3>Note History</h3>
              <p>As audio is recorded, the detected notes appear on top. The louder the note, the darker the shading.</p>
              <p>Blue shading is normal. Yellowish shading means the note doesn't fit into the detected scale.</p>
              {/* <p>
                Visit the settings <OpenSettingsMenuButton menuName={MenuName.ChordHistoryOptions} /> to change how notes are displayed.
              </p> */}
            </InfoCard>
            <InfoCard>
              <h3>Chord History</h3>
              <p>For each scale which fits recent notes, the possible chords appear below the notes.</p>
              <p>It will show you as many of the best-fitting chords as it can find, so you can choose which one makes sense.</p>
            </InfoCard>
            <InfoCard>
              <h3>History Replay</h3>
              <p>
                Once you've recorded or processed some audio, the {inlineIcon(ICON_PLAY)} button will allow you to play the chord
                representation of that audio.
              </p>
              <p>
                It'll choose the topmost chord in the primary scale. You can use chord delete mode to get rid of chords (and keys) you don't
                want to hear.
              </p>
              <p>Play with the arpeggiation setting to make this more interesting.</p>
            </InfoCard>
            {/* <InfoCard>
              <h3>History Download/Upload</h3>
              <p>
                If you'd like to save the chords from a session, you can press the {inlineIcon(ICON_DOWNLOAD)} button to save it as a file.
              </p>
              <p>
                Then on a later visit you can press the {inlineIcon(ICON_UPLOAD)} button to view the same set of chords without re-doing
                your recording.
              </p>
            </InfoCard> */}
            <InfoCard>
              <h2>Tablature Builder</h2>
            </InfoCard>
            <InfoCard>
              <p>You can select chords from History to be translated into guitar/ukelele/other tablature.</p>
              <p>Try it and see. Or, the video playlist at the top of this Help page walks through how to use it.</p>
            </InfoCard>
            <InfoCard>
              <h2>Frequently Asked Questions</h2>
            </InfoCard>
            <InfoCard>
              <h3>What's the point?</h3>
              <p>Fun, mostly. This is a tool for song writers, cover artists, and ear-oriented musicians of all stripes.</p>
              <p>If you think up a melody, you can sing it into this tool and get some chord ideas.</p>
              <p>Or you can use it to get inspiration for a solo on top of a progression.</p>
            </InfoCard>
            <InfoCard>
              <h3>Why is it called "Noodler"?</h3>
              <p>
                In music, "noodling" refers to playing in an unstructured or improvisational manner. It can be used disparagingly to suggest
                that the person playing the music doesn't know much music theory.
              </p>
              <p>Elsewhere in USA slang, your "Noodle" is your brain and "noodling" means brainstorming.</p>
              <p>So the name is a joke re: my own weakness as a musician, and a shrug re: any judgements about it.</p>
            </InfoCard>
            <InfoCard>
              <h3>How does it work?</h3>
              <p>
                Music theory, signal processing math, and heuristics. Histograms are generated for overlapping slices of time using Hanning
                windows and Fourier transforms. Then those frequencies are converted to notes, based on standard frequencies for Equal
                Temperament tuning.
              </p>
              <p>
                Then there's noise reduction using the differences in the overlapping time slices, recent audio history, and math about
                overtones.
              </p>
              <p>
                Finally, the remaining notes can only fit a subset of possible scales, and the chords within those scales. So wrong notes
                are automatically disabled each time a key change occurs.
              </p>
              <p>
                The hard parts are done in{" "}
                <a target="_blank" rel="noreferrer" href="https://en.wikipedia.org/wiki/WebAssembly">
                  WebAssembly {"\u{2728}"}
                </a>
                , which is how it's able to run on your machine with no internet access.
              </p>
            </InfoCard>
            <InfoCard>
              <h3>Does it use AI?</h3>
              <p>No, just math and human-written code.</p>
            </InfoCard>
            <InfoCard>
              <h3>Why not process the whole file at once?</h3>
              <p>
                There are several other websites which do that. The histogram of an entire file will ignore key changes and pentatonic
                moments, whereas the idea here is to allow full human control of section-by-section processing.
              </p>
              <p>
                If you jump to a different part of the audio, it will retain the key information from where you started. So if you want to
                re-process the beginning of the file with a specific key as the context, you can easily do so.
              </p>
            </InfoCard>
            <InfoCard>
              <h3>Why can't I feed it a YouTube /streaming URL?</h3>
              <p>
                Google's terms prohibit separating out the audio from a youtube video. US law around interoperability{" "}
                <a target="_blank" rel="noreferrer" href="https://www.eff.org/deeplinks/2020/07/legislative-path-interoperable-internet">
                  needs a rework
                </a>
                .
              </p>
              <p>However, if you google "download youtube audio free" you'll find a number of tools exist for that purpose.</p>
              <p>I of course don't recommend using those unless you're legally allowed to do so.</p>
            </InfoCard>
            <InfoCard>
              <h3>The website got something wrong, what gives?</h3>
              <p>
                Histograms across finite slices of time are inherently faulty. And if the tuning is slightly off, or if the time windows
                happen to slice transitional moments and get weird information, you'll sometimes see wrong/extra notes.
              </p>
              <p>Automatic note pinning usually eliminates those extra notes. But if it doesn't work you can switch to manual mode.</p>
              <p>
                Also note, you can seek to anywhere in the audio and re-process that section. For example, seeking back to the beginning
                once the scale is identified will yield a cleaner read because the automation now knows which notes to ignore.
              </p>
              <p>
                The website errs on the side of showing too many chord/scale possibilities, so that you can click the chords and see what
                sounds right to you.
              </p>
            </InfoCard>
            <InfoCard>
              <h3>How come the audio glitches sometimes?</h3>
              <p>
                The data streaming between HTML audio and WebAssembly code can sometimes get junk in it, such that the packets can no longer
                be parsed. When that happens, the site tries to autorecover by resetting the HTML audio so it'll re-initiate a valid stream.
                That auto-recovery is what causes miniature jumps back in time. Which is a bit annoying, but less bad than not recovering at
                all.
              </p>
              <p>If you notice that happening repeatedly, you can usually refresh the page to fix it.</p>
            </InfoCard>
            <InfoCard>
              <h3>The website is completely broken!</h3>
              <p>
                First, try a different browser. Chrome will be the most reliable because it's where I test the most. Every browser has
                slightly different behavior around audio processing and WebAssembly, and I'm only one person, so it's likely I'll miss
                things, especially if you're using an obscure browser.
              </p>
            </InfoCard>
            {!IS_WEBM_PCM_SUPPORTED && (
              <InfoCard>
                <h3>Why are results inconsistent on Firefox/Safari?</h3>
                <p>
                  As of 2025 Firefox and Safari don't support container formats which would let me reliably transfer{" "}
                  <a target="_blank" href="https://en.wikipedia.org/wiki/Pulse-code_modulation" rel="noreferrer">
                    PCM
                  </a>{" "}
                  encoded audio data.
                </p>
                <p>
                  I had to work around that using the javascript audio API to get raw PCM data. But the limitation of that API is it
                  introduces discontinuities (missing data), and when those occur is semi-random for complicated reasons.
                </p>
                <p>If you use Firefox to avoid advertisements, good news: there are none.</p>
              </InfoCard>
            )}
            {!IS_WEBM_PCM_SUPPORTED && (
              <InfoCard>
                <h3>Why does the site look weird on Safari?</h3>
                <p>
                  I can't run Safari on a Windows PC, and I don't have a Mac at the moment. And Apple's legal terms prohibit me from using
                  Safari in a VM on non-apple hardware.
                </p>
                <p>
                  If Apple wanted free websites to work on their special browser, they'd meet me halfway. As it stands, they insist on a bad
                  user experience.
                </p>
                <p>If you need other options, Chrome, Brave, and MS Edge all work on Mac.</p>
              </InfoCard>
            )}
            <InfoCard>
              <h3>Bug Report or Feature Request</h3>
              <p>
                If you'd like to report a bug, please email <a href="mailto:info@chordnoodler.com">info@chordnoodler.com</a> with
                screenshots/video and a clear description of the problem.
              </p>
              <p>To set expectations clearly: this is not my day job and I can't guarantee you a response.</p>
            </InfoCard>
          </>
        );

      default:
        return <div>Oh no! This help content is not yet defined</div>;
    }
  }

  render(): ReactNode {
    return this.context.showHelpPopup ? (
      <CloseableModal onClose={this.handleCloseModal}>
        <div className="help-popup-content">{this.renderHelpContent()}</div>
      </CloseableModal>
    ) : undefined;
  }
}

// Not sure if actually needed.  The context knows whether this thing is open... must close it.
HelpPopup.contextType = AppContextType;

export default HelpPopup;
